import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { DiagnosticsConfig } from "../lib/types";
import { DIAGNOSTIC_META } from "../lib/diagnostics";
import { LIFECYCLE_META } from "../lib/lifecycle";
import { PageHeader, Card } from "../components/ui";
import { Badge, type BadgeTone } from "../components/Badge";

/**
 * Metrics & Diagnostics — plain-language explanation of every flag and pill,
 * with the exact trigger underneath. Readable by clients (they can log in).
 * Reflects the currently configured thresholds so the docs never drift.
 */
export function Docs() {
  const { data } = useQuery({
    queryKey: ["diagnosticsConfig"],
    queryFn: () => api.get<DiagnosticsConfig>("/api/config/diagnostics"),
  });

  const d = data?.diagnosticThresholds;
  const f = data?.adFlagThresholds;
  const a = data?.alertThresholds;
  const l = data?.lifecycleThresholds;
  const pct = (v: number | undefined, fallback: number) => Math.round((v ?? fallback) * 100);

  return (
    <div>
      <PageHeader
        title="Metrics & Diagnostics"
        subtitle="What every flag and pill means, and exactly how it's calculated."
      />

      <Card className="mb-6">
        <h2 className="mb-2 text-sm font-semibold text-ink">The funnel</h2>
        <p className="text-sm text-ink-secondary">
          Every result travels the same path. Each diagnostic isolates{" "}
          <span className="text-ink">where</span> a struggling campaign breaks along it:
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          {["Impression", "Click", "Landing", "Conversion"].map((s, i) => (
            <span key={s} className="flex items-center gap-2">
              <span className="rounded-full border border-hairline bg-surface-2 px-2.5 py-1 text-ink-secondary">
                {s}
              </span>
              {i < 3 && <span className="text-muted">→</span>}
            </span>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted">
          <span className="text-ink-secondary">Impression → Click</span> is measured by{" "}
          <span className="text-ink-secondary">CTR</span> (the ad's pull).{" "}
          <span className="text-ink-secondary">Click → Conversion</span> is the{" "}
          <span className="text-ink-secondary">conversion rate</span> (the post-click experience).
          An <span className="text-ink-secondary">upstream</span> problem is before the click; a{" "}
          <span className="text-ink-secondary">downstream</span> problem is after it. All comparisons
          are the selected window vs the prior window of equal length, with a{" "}
          <b>{pct(d?.deadband, 0.05)}% deadband</b> so small wobbles don't trigger anything.
        </p>
      </Card>

      <Section title="Diagnostic pills">
        <p className="text-xs text-muted">
          Only problems get a pill — a healthy campaign shows none. A campaign can show several at
          once. No pill is shown unless the campaign had at least{" "}
          <b>{(d?.minImpressions ?? 500).toLocaleString()}</b> impressions and{" "}
          <b>{(d?.minClicks ?? 20).toLocaleString()}</b> clicks in the window — thin data is never
          diagnosed. Hover any pill to see the exact numbers that fired it.
        </p>
        <DocCard
          tone="warning"
          pill={DIAGNOSTIC_META.upstream.label}
          plain={DIAGNOSTIC_META.upstream.meaning}
          formula={`CTR is down AND cost per conversion is up (each beyond the ${pct(d?.deadband, 0.05)}% deadband), vs the prior window.`}
          action={`Check ${DIAGNOSTIC_META.upstream.action}`}
        />
        <DocCard
          tone="warning"
          pill={DIAGNOSTIC_META.downstream.label}
          plain={DIAGNOSTIC_META.downstream.meaning}
          formula={`CTR holds or rises AND conversions are down AND cost per conversion is up. (Conversions collapsing to zero counts as cost per conversion "up".)`}
          action={`Check ${DIAGNOSTIC_META.downstream.action}`}
        />
        <DocCard
          tone="warning"
          pill={DIAGNOSTIC_META.wrong_clicks.label}
          plain={DIAGNOSTIC_META.wrong_clicks.meaning}
          formula={`CTR ≥ ${d?.wrongClicksCtrRatio ?? 1.2}× this client's median CTR AND conversion rate ≤ ${d?.wrongClicksConvRatio ?? 0.5}× the median conversion rate — relative to your other campaigns, never an absolute number.`}
          action={`Check ${DIAGNOSTIC_META.wrong_clicks.action}`}
        />
        <DocCard
          tone="warning"
          pill={DIAGNOSTIC_META.fatigue_incoming.label}
          plain={DIAGNOSTIC_META.fatigue_incoming.meaning}
          formula={`On an active, spending campaign with ≥ ${d?.minTrendDays ?? 4} days of data: average CTR in the later half of the window is ≥ ${pct(d?.fatigueTrendDrop, 0.15)}% below the earlier half. A leading indicator — it fires before the age-based Creative Fatigue flag.`}
          action={`Check ${DIAGNOSTIC_META.fatigue_incoming.action}`}
        />
      </Section>

      <Section title="Status flags">
        <DocCard
          tone="critical"
          pill="Drop"
          plain="Week-over-week performance got meaningfully worse."
          formula={`Conversions fell ≥ ${pct(a?.convDrop, 0.15)}% (or to zero) OR cost per conversion rose ≥ ${pct(a?.cpcRise, 0.15)}%.`}
          action="Open the campaign; the diagnostic pills say where the problem is."
        />
        <DocCard
          tone="good"
          pill="Rising"
          plain="Week-over-week performance improved strongly."
          formula={`Conversions climbed ≥ ${pct(a?.rise, 0.15)}% (or up from zero) OR cost per conversion fell ≥ ${pct(a?.rise, 0.15)}%.`}
          action="Consider scaling budget while it's working."
        />
        <DocCard
          tone="critical"
          pill="Creative Fatigue"
          plain="A live ad's creative is old enough that audiences have likely seen it too many times."
          formula={`An ACTIVE, spending ad whose creative has been live longer than ${f?.creativeFatigueDays ?? 42} days.`}
          action="Refresh the creative. (Different from Fatigue Incoming, which is about the CTR trend, not age.)"
        />
        <DocCard
          tone="critical"
          pill="High Frequency"
          plain="The same people are seeing the ads too often, which burns out an audience."
          formula={`Average frequency (impressions ÷ reach) over the window exceeds ${f?.highFrequency ?? 2.5}.`}
          action="Broaden the audience or rotate creatives."
        />
      </Section>

      <Section title="Lifecycle context (blue)">
        <p className="text-xs text-muted">
          Not problems — context that changes how you read the numbers. A new or
          recently-changed object is still settling (give it room to grow); one that's
          ending soon tapers by design. Shown on the campaign and inside its ad-set / ad
          breakdown; a campaign rolls these up from its ad-sets + the change-log.
        </p>
        <DocCard
          tone="info"
          pill={LIFECYCLE_META.new.label}
          plain={LIFECYCLE_META.new.meaning}
          formula={`Created within the last ${l?.newDays ?? 7} days (a campaign uses its earliest ad-set).`}
        />
        <DocCard
          tone="info"
          pill={LIFECYCLE_META.recently_modified.label}
          plain={LIFECYCLE_META.recently_modified.meaning}
          formula={`A budget / creative / targeting / status change in the last ${l?.modifiedDays ?? 7} days (from the activity change-log; for an ad, its creative was swapped). "New" takes precedence over this.`}
        />
        <DocCard
          tone="info"
          pill={LIFECYCLE_META.ending_soon.label}
          plain={LIFECYCLE_META.ending_soon.meaning}
          formula={`An ACTIVE ad-set scheduled to end within the next ${l?.endingDays ?? 7} days (rolled up to its campaign).`}
        />
      </Section>

      <Section title="The metrics">
        <DocCard tone="muted" pill="CTR" plain="Click-through rate — how compelling the ad is." formula="clicks ÷ impressions." />
        <DocCard tone="muted" pill="Cost / conversion" plain="What each result costs you." formula="spend ÷ conversions." />
        <DocCard tone="muted" pill="Conversion rate" plain="How well clicks turn into results (the post-click experience)." formula="conversions ÷ clicks." />
        <DocCard tone="muted" pill="ROAS" plain="Return on ad spend — revenue earned per unit spent." formula="revenue ÷ spend (both in MUR)." />
        <DocCard tone="muted" pill="Profit ROAS" plain="ROAS on the profit the revenue actually produces. Break-even is 1.0× — below that, ads cost more than the profit they drive." formula="(revenue × margin) ÷ spend." />
      </Section>

      <p className="text-xs text-muted">
        These are deterministic rules — no AI. Every threshold above is tunable by an admin in{" "}
        <span className="text-ink-secondary">Settings</span>, so this page always reflects the live
        configuration.
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-base font-semibold text-ink">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

type Tone = "warning" | "critical" | "good" | "muted" | "info";
const BADGE_TONE: Record<Tone, BadgeTone> = {
  warning: "warning",
  critical: "critical",
  good: "good",
  info: "info",
  muted: "neutral",
};

function DocCard({
  pill,
  tone,
  plain,
  formula,
  action,
}: {
  pill: string;
  tone: Tone;
  plain: string;
  formula?: string;
  action?: string;
}) {
  return (
    <Card>
      <Badge tone={BADGE_TONE[tone]} size="md">
        {pill}
      </Badge>
      <p className="mt-2 text-sm text-ink-secondary">{plain}</p>
      {formula && (
        <p className="mt-2 rounded-md bg-surface-2 px-3 py-2 text-xs text-muted">
          <span className="font-medium text-ink-secondary">Fires when:</span> {formula}
        </p>
      )}
      {action && (
        <p className="mt-2 text-xs text-muted">
          <span className="font-medium text-ink-secondary">What to do:</span> {action}
        </p>
      )}
    </Card>
  );
}
